import { Component, ContentChildren, EventEmitter, Output, QueryList } from '@angular/core';
import { StackCardDirective } from './stack-card.directive';

@Component({
  selector: 'rosie-card-stack',
  templateUrl: './card-stack.component.html',
  styleUrls: ['./card-stack.component.scss'],
  standalone: false
})
export class CardStackComponent {
  @ContentChildren(StackCardDirective) cardDefs: QueryList<StackCardDirective>;
  @Output() cardDismissed = new EventEmitter<string>();

  private static readonly MAX_PEEKS = 2;

  private dismissedIds = new Set<string>();
  dismissingId: string | null = null;

  get visibleCards(): StackCardDirective[] {
    if (!this.cardDefs) return [];
    return this.cardDefs
      .toArray()
      .filter(c => c.rosieStackCardVisible && !this.dismissedIds.has(c.rosieStackCardId));
  }

  // Slot list: front card plus up to MAX_PEEKS real cards behind it.
  // The tab count should reflect actual remaining cards: two peeks until the
  // second-to-last card, then one peek for the final card.
  get slots(): StackCardDirective[] {
    return this.visibleCards.slice(0, 1 + CardStackComponent.MAX_PEEKS);
  }

  isPromoting(index: number): boolean {
    return this.dismissingId !== null && index === 1;
  }

  getCardStyle(index: number, card: StackCardDirective): Record<string, string> {
    if (this.dismissingId === card.rosieStackCardId) {
      return {
        opacity: '0',
        transform: 'translateY(20px) scale(1)',
        'z-index': String(100 - index),
        'pointer-events': 'none'
      };
    }

    // Promote the next card to top position while the top card is dismissing
    if (this.isPromoting(index)) {
      return {
        opacity: '1',
        transform: 'translateY(0) scale(1)',
        'z-index': String(100 - index),
        'pointer-events': 'none'
      };
    }

    const scale = 1 - 0.12 * index;
    const y = -(4 * index);

    return {
      opacity: '1',
      transform: `translateY(${y}px) scale(${scale})`,
      'z-index': String(100 - index),
      'pointer-events': index === 0 && card ? 'auto' : 'none'
    };
  }

  // Track cards by id so they keep their DOM element when promoted.
  trackBySlot(_index: number, slot: StackCardDirective): string {
    return slot.rosieStackCardId;
  }

  dismiss(): void {
    if (this.dismissingId) return;
    const topCard = this.visibleCards[0];
    if (!topCard?.rosieStackCardDismissible) return;

    this.dismissingId = topCard.rosieStackCardId;

    setTimeout(() => {
      this.dismissedIds.add(this.dismissingId);
      this.cardDismissed.emit(this.dismissingId);
      this.dismissingId = null;
    }, 280);
  }
}
